import type { DiagramSpec } from '../types'

export const vidmaxDiagram: DiagramSpec = {
  id: 'vidmax-pipeline',
  name: 'VidMax Pipeline Map',
  description: 'Use cases → pipelines → models for video generation',
  layers: [
    { id: 'use-cases', name: 'Use Cases', color: '#6366f1' },
    { id: 'pipelines', name: 'Pipelines', color: '#f59e0b' },
    { id: 'models', name: 'Models', color: '#10b981' },
  ],
  nodes: [
    {
      id: 'uc-education',
      label: 'Educational Content',
      layer: 'use-cases',
      rank: 0,
      description: 'Tutorials, explainers, how-to videos',
    },
    {
      id: 'uc-marketing',
      label: 'Marketing Videos',
      layer: 'use-cases',
      rank: 0,
      description: 'Product demos, ads, brand content',
    },
    {
      id: 'uc-entertainment',
      label: 'Entertainment',
      layer: 'use-cases',
      rank: 0,
      description: 'Faceless channels, viral content',
    },
    {
      id: 'uc-social',
      label: 'Social Content',
      layer: 'use-cases',
      rank: 0,
      description: 'Shorts, Reels, TikTok clips',
    },

    {
      id: 'p-talking-head',
      label: 'Talking Head',
      layer: 'pipelines',
      rank: 1,
      description: 'AI avatar speaks to camera',
      fields: { Output: 'MP4', 'Avg Duration': '2–10 min', Complexity: 'Medium' },
    },
    {
      id: 'p-animation',
      label: 'Animation',
      layer: 'pipelines',
      rank: 1,
      description: 'Generated visual sequences',
      fields: { Output: 'MP4', 'Avg Duration': '30s–3 min', Complexity: 'High' },
    },
    {
      id: 'p-slideshow',
      label: 'Slideshow',
      layer: 'pipelines',
      rank: 1,
      description: 'Static images + voiceover',
      fields: { Output: 'MP4', 'Avg Duration': '1–5 min', Complexity: 'Low' },
    },
    {
      id: 'p-shorts',
      label: 'Shorts',
      layer: 'pipelines',
      rank: 1,
      description: 'Vertical short-form clips',
      fields: { Output: 'MP4', 'Avg Duration': '15–60s', Complexity: 'Low' },
    },

    {
      id: 'm-claude',
      label: 'Claude Sonnet',
      layer: 'models',
      rank: 2,
      description: 'Script & copy generation',
      fields: { Provider: 'Anthropic', Use: 'Script', Cost: '$$' },
    },
    {
      id: 'm-gpt4',
      label: 'GPT-4o',
      layer: 'models',
      rank: 2,
      description: 'Script generation alternative',
      fields: { Provider: 'OpenAI', Use: 'Script', Cost: '$$' },
    },
    {
      id: 'm-runway',
      label: 'Runway Gen-3',
      layer: 'models',
      rank: 2,
      description: 'Text-to-video generation',
      fields: { Provider: 'Runway', Use: 'Video Gen', Cost: '$$$' },
    },
    {
      id: 'm-elevenlabs',
      label: 'ElevenLabs',
      layer: 'models',
      rank: 2,
      description: 'Voice synthesis',
      fields: { Provider: 'ElevenLabs', Use: 'Voiceover', Cost: '$$' },
    },
    {
      id: 'm-heygen',
      label: 'HeyGen',
      layer: 'models',
      rank: 2,
      description: 'AI avatar video',
      fields: { Provider: 'HeyGen', Use: 'Avatar', Cost: '$$$' },
    },
    {
      id: 'm-sora',
      label: 'Sora',
      layer: 'models',
      rank: 2,
      description: 'High-quality video generation',
      fields: { Provider: 'OpenAI', Use: 'Video Gen', Cost: '$$$$' },
    },
  ],
  edges: [
    { id: 'e-ed-th', source: 'uc-education', target: 'p-talking-head' },
    { id: 'e-ed-ss', source: 'uc-education', target: 'p-slideshow' },
    { id: 'e-mkt-th', source: 'uc-marketing', target: 'p-talking-head' },
    { id: 'e-mkt-an', source: 'uc-marketing', target: 'p-animation' },
    { id: 'e-ent-an', source: 'uc-entertainment', target: 'p-animation' },
    { id: 'e-ent-ss', source: 'uc-entertainment', target: 'p-slideshow' },
    { id: 'e-soc-sh', source: 'uc-social', target: 'p-shorts' },
    { id: 'e-soc-th', source: 'uc-social', target: 'p-talking-head' },

    { id: 'e-th-heygen', source: 'p-talking-head', target: 'm-heygen' },
    { id: 'e-th-11labs', source: 'p-talking-head', target: 'm-elevenlabs' },
    { id: 'e-th-claude', source: 'p-talking-head', target: 'm-claude' },
    { id: 'e-an-runway', source: 'p-animation', target: 'm-runway' },
    { id: 'e-an-sora', source: 'p-animation', target: 'm-sora' },
    { id: 'e-an-claude', source: 'p-animation', target: 'm-claude' },
    { id: 'e-ss-claude', source: 'p-slideshow', target: 'm-claude' },
    { id: 'e-ss-11labs', source: 'p-slideshow', target: 'm-elevenlabs' },
    { id: 'e-sh-claude', source: 'p-shorts', target: 'm-claude' },
    { id: 'e-sh-heygen', source: 'p-shorts', target: 'm-heygen' },
    { id: 'e-sh-runway', source: 'p-shorts', target: 'm-runway' },
  ],
  views: [
    { id: 'full', name: 'Full Map', layout: 'TB' },
    {
      id: 'pipelines-models',
      name: 'Pipelines → Models',
      layers: ['pipelines', 'models'],
      layout: 'LR',
    },
    {
      id: 'use-cases-pipelines',
      name: 'Use Cases → Pipelines',
      layers: ['use-cases', 'pipelines'],
      layout: 'TB',
    },
    {
      id: 'models-only',
      name: 'Models Only',
      layers: ['models'],
      layout: 'LR',
    },
  ],
  defaultView: 'full',
}
